import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';

import { getWorkshopVehicleDetails } from '../api/dms/workshopVehicle.api';
import { logConsole, logTransaction } from '../common/log';
import { handleThunkError } from '../common/util';
import { AttachmentAction, AttachmentAssetType, IAttachment, IAttachmentContainer } from '../models/dms/attachment.model';
import { IWorkshopVehicleDetails } from '../models/dms/workshopVehicle.model';
import { LogType } from '../models/log.model';
import { ConfigName, updateConfigObject } from '../sqlite/common/config';
import { setIsLoadingCountState } from './appSlice';
import { setSelectedVehicleAttachmentsStoreAsync } from './vehicleAttachmentSlice';

interface IWorkshopVehicleState {
  selectedWorkshopVehicle?: IWorkshopVehicleDetails;
  selectedWorkshopVehicleLoading: boolean;
}

const initialState: IWorkshopVehicleState = {
  selectedWorkshopVehicle: undefined,
  selectedWorkshopVehicleLoading: false,
};

/// THUNKS

export const setSelectedWorkshopVehicleStoreAsync = createAsyncThunk<{}, IWorkshopVehicleDetails>('workshopVehicle/setSelectedWorkshopVehicleStoreAsync', async (workshopVehicle, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting selected workshop vehicle '${workshopVehicle.registrationNumber}' to storage`, '[workshopVehicle] setSelectedWorkshopVehicleStoreAsync');
  try {
    dispatch(setSelectedWorkshopVehicleState(workshopVehicle));
    await updateConfigObject<IWorkshopVehicleDetails>(ConfigName.SelectedWorkshopVehicle, workshopVehicle, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const fetchWorkshopVehicleDetailsByRegistrationNumberStoreAsync = createAsyncThunk<{}, { registrationNumber: string; repairOrderNumber: number }>(
  'workshopVehicle/fetchWorkshopVehicleDetailsByRegistrationNumberStoreAsync',
  async ({ registrationNumber, repairOrderNumber }, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, `Fetching workshop vehicle details from database - registrationNumber: ${registrationNumber}, repairOrderNumber: ${repairOrderNumber}`, '[workshopVehicle] fetchWorkshopVehicleDetailsByRegistrationNumberStoreAsync');
    try {
      dispatch(setIsLoadingCountState(1));
      dispatch(setSelectedWorkshopVehicleLoadingState(true));

      const state = getState() as RootState;

      let attachmentContainer = {
        assetType: AttachmentAssetType.WorkshopVehicle,
        key: registrationNumber,
        jobCode: '',
        jobType: '',
        repairOrderNumber: repairOrderNumber.toString(),
        mediaRef: '',
        attachments: state.vehicleAttachments.selectedVehicleAttachments?.key === registrationNumber ? [...state.vehicleAttachments.selectedVehicleAttachments.attachments] : [],
      } as IAttachmentContainer;

      await dispatch(setSelectedVehicleAttachmentsStoreAsync(attachmentContainer));

      const responseData = await getWorkshopVehicleDetails(registrationNumber);
      await dispatch(setSelectedWorkshopVehicleStoreAsync(responseData));

      // Be sure to include existing unsync attachments
      const { selectedVehicleAttachments } = state.vehicleAttachments;
      const outstandingAttachments = selectedVehicleAttachments?.attachments.filter((a) => a.action === AttachmentAction.Add) || [];

      // Set the vehicle attachments
      attachmentContainer = {
        ...attachmentContainer,
        attachments: [...responseData.attachments.map((a) => ({ ...a, assetType: AttachmentAssetType.WorkshopVehicle })), ...outstandingAttachments] as IAttachment[],
      } as IAttachmentContainer;

      await dispatch(setSelectedVehicleAttachmentsStoreAsync(attachmentContainer));

      dispatch(setIsLoadingCountState(-1));
      dispatch(setSelectedWorkshopVehicleLoadingState(false));

      return responseData;
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setSelectedWorkshopVehicleLoadingState(false));
    }
  },
);

/// SLICE

export const workshopVehicleSlice = createSlice({
  name: 'workshopVehicle',
  initialState,
  reducers: {
    setSelectedWorkshopVehicleState: (state, action: PayloadAction<IWorkshopVehicleDetails>) => {
      logConsole(LogType.Info, '[workshopVehicle] setSelectedWorkshopVehicleState');
      state.selectedWorkshopVehicle = action.payload;
    },
    setSelectedWorkshopVehicleLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[workshopVehicle] setSelectedWorkshopVehicleLoadingState: ${action.payload}`);
      state.selectedWorkshopVehicleLoading = action.payload;
    },
  },
});

/// REDUCERS

export const { setSelectedWorkshopVehicleState, setSelectedWorkshopVehicleLoadingState } = workshopVehicleSlice.actions;

/// CUSTOM SELECTORS

export default workshopVehicleSlice.reducer;
